import { createContext, useCallback, useContext, useLayoutEffect, useState } from "react";

/* EchoSense theme (design-system-001).
   Light is the brand default; dark is an explicit, persisted user choice
   (flipped from dark-default 2026-07-09 per operator directive). There is
   intentionally NO OS-follow — light stays the default regardless of
   `prefers-color-scheme`. Print is forced to light by the stylesheet,
   independent of this control.

   CSS architecture note: the token stylesheet keeps DARK on `:root` and LIGHT
   on `[data-theme="light"]`, so "default to light" is expressed by ALWAYS
   applying `data-theme="light"` for the default/unset case (see applyTheme).
   The pre-React first paint is handled by the inline bootstrap in
   public/index.html so there is no flash of dark before this provider mounts.

   STORAGE_KEY was bumped to `-v2` on the light cutover: the previous provider
   auto-persisted the (then dark) default to storage on every mount, so old
   values can't be distinguished from a real choice. A fresh key gives everyone
   the new light default; a genuine dark preference is re-persisted the moment
   the user toggles. This provider now ONLY writes storage on an explicit
   setTheme — an unset key means "use the default", cleanly. */

const STORAGE_KEY = "es-theme-v2";

/** Apply a theme to <html>. Dark is the :root default in the stylesheet, so
 *  we set the attribute for LIGHT (the default + explicit-light) and remove it
 *  only for an explicit DARK choice. */
export function applyTheme(theme) {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  if (theme === "dark") el.removeAttribute("data-theme");
  else el.setAttribute("data-theme", "light");
}

export function getStoredTheme() {
  if (typeof window === "undefined") return "light";
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

// null default so a missing provider is detectable (mirrors useAuth/useLanguage).
const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getStoredTheme);

  // Apply before paint so there's no flash of the wrong theme.
  useLayoutEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    const value = next === "dark" ? "dark" : "light";
    setThemeState(value);
    // Persist ONLY on an explicit user choice — an unset key means "default".
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* storage unavailable — keep the in-memory choice */
    }
  }, []);

  return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (ctx === null) {
    throw new Error("useTheme must be used within a <ThemeProvider>");
  }
  return ctx;
}
